import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  BatteryLow,
  BookHeart,
  CalendarDays,
  Check,
  CloudFog,
  CloudRain,
  Flame,
  Heart,
  Leaf,
  Loader2,
  LockKeyhole,
  NotebookPen,
  Sparkles,
  Sprout,
  Stars,
  Sun,
  WandSparkles,
  Waves,
  type LucideIcon,
} from "lucide-react";
import { z } from "zod";
import { AiConsentDialog, hasAiConsent } from "@/components/AiConsentDialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { readSavedJournalEntry } from "@/lib/journal-reading.functions";
import { withMemberGate } from "@/lib/with-member-gate";

const MOOD_ICONS: Record<string, LucideIcon> = {
  anxious: Waves,
  sad: CloudRain,
  frustrated: Flame,
  exhausted: BatteryLow,
  numb: CloudFog,
  calm: Leaf,
  hopeful: Sprout,
  grateful: Heart,
  strong: Sun,
  thriving: Stars,
};

type Entry = { id: string; title: string; content: string; mood: string | null; prompt: string | null; created_at: string };

const searchSchema = z.object({ entry: z.string().optional().catch(undefined) });

export const Route = createFileRoute("/journal-reading")({
  validateSearch: (search) => searchSchema.parse(search),
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Gentle Journal Reading — Recovery Trauma™" },
      { name: "description", content: "A private, gentle reflection on one of your saved journal entries." },
      { property: "og:title", content: "Gentle Journal Reading — Recovery Trauma™" },
      { property: "og:description", content: "A private, gentle reflection on one of your saved journal entries." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: withMemberGate(JournalReadingPage, "Journal Reading"),
});

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });

function JournalReadingPage() {
  const { entry: requestedId } = Route.useSearch();
  const createReading = useServerFn(readSavedJournalEntry);
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["journal-reading-entries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("journal_entries")
        .select("id, title, content, mood, prompt, created_at, audio_path")
        .is("audio_path", null)
        .order("created_at", { ascending: false })
        .limit(60);
      if (error) throw error;
      return (data ?? []).filter((e) => e.content.trim()) as Entry[];
    },
  });
  const [selectedId, setSelectedId] = useState(requestedId ?? "");
  const [reading, setReading] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [consentOpen, setConsentOpen] = useState(false);

  useEffect(() => {
    if (!selectedId && entries[0]) setSelectedId(entries[0].id);
  }, [entries, selectedId]);

  const selected = useMemo(() => entries.find((e) => e.id === selectedId) ?? entries[0], [entries, selectedId]);

  async function requestReading() {
    if (!selected || loading) return;
    setLoading(true);
    setError("");
    setReading("");
    try {
      const result = await createReading({ data: { entryId: selected.id } });
      setReading(result.reading);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "We couldn't create this reading. Your saved entry is unchanged.");
    } finally {
      setLoading(false);
    }
  }

  const beginReading = () => (hasAiConsent() ? void requestReading() : setConsentOpen(true));

  if (isLoading) {
    return (
      <div className="grid min-h-[55vh] place-items-center">
        <Loader2 className="h-7 w-7 animate-spin text-[var(--gold-deep)]" aria-label="Loading saved entries" />
      </div>
    );
  }

  return (
    <main className="min-h-dvh bg-background pb-[calc(env(safe-area-inset-bottom)+6rem)] lg:pb-16">
      <div className="mx-auto w-full max-w-5xl px-4 pb-8 pt-4 sm:px-6 sm:pt-8">
        <header className="relative overflow-hidden border-b border-gold/30 px-2 pb-7 pt-4 text-center sm:pb-9">
          <Leaf className="absolute start-2 top-7 h-9 w-9 text-muted-foreground/25" aria-hidden />
          <Stars className="absolute end-3 top-3 h-8 w-8 text-[var(--gold-deep)]/55" aria-hidden />
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full border border-gold/40 bg-[var(--qa-gold-tint)] text-[var(--gold-deep)] shadow-sm">
            <NotebookPen className="h-8 w-8" aria-hidden />
          </span>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">Your private journal space</p>
          <h1 className="mt-1 font-display text-3xl text-foreground sm:text-4xl">Gentle Journal Reading</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Choose one saved entry and receive gentle possibilities to explore — never a diagnosis or fixed meaning.
          </p>
        </header>

        {entries.length === 0 ? (
          <section className="mx-auto mt-8 max-w-xl border-y border-gold/25 py-10 text-center">
            <BookHeart className="mx-auto h-9 w-9 text-[var(--gold-deep)]" aria-hidden />
            <h2 className="mt-3 font-display text-2xl text-foreground">No saved entries yet</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Save a reflection first, then return here whenever you would like a gentle reading.
            </p>
            <Button asChild variant="gold" className="mt-5 rounded-full">
              <Link to="/journal">Open Recovery Journal</Link>
            </Button>
          </section>
        ) : (
          <div className="mt-7 grid gap-7 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-start">
            <section aria-labelledby="choose-entry-heading">
              <h2 id="choose-entry-heading" className="font-display text-xl text-foreground">Choose a saved entry</h2>
              <div className="mt-3 max-h-[26rem] space-y-2 overflow-y-auto pe-1 lg:max-h-none">
                {entries.map((entry) => {
                  const active = selected?.id === entry.id;
                  const Icon = MOOD_ICONS[entry.mood?.toLowerCase() ?? ""] ?? NotebookPen;
                  return (
                    <Button
                      key={entry.id}
                      type="button"
                      variant="outline"
                      aria-pressed={active}
                      onClick={() => { setSelectedId(entry.id); setReading(""); setError(""); }}
                      className={`h-auto w-full justify-start whitespace-normal rounded-2xl px-4 py-3 text-start ${active ? "border-gold bg-gold/15 ring-2 ring-gold ring-offset-2 ring-offset-background" : "border-border/70 bg-card"}`}
                    >
                      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${active ? "bg-gold/25 text-[var(--gold-deep)]" : "bg-muted text-muted-foreground"}`}>
                        {active ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-display text-base text-foreground">
                          {entry.title || entry.content.slice(0, 48)}
                        </span>
                        <span className="mt-0.5 flex items-center gap-1 text-xs font-normal text-muted-foreground">
                          <CalendarDays className="h-3 w-3" /> {fmtDate(entry.created_at)}
                        </span>
                      </span>
                    </Button>
                  );
                })}
              </div>
            </section>

            {selected ? (
              <section className="overflow-hidden rounded-3xl border border-gold/35 bg-card shadow-[var(--shadow-soft-lg)]">
                <div className="border-b border-gold/25 bg-[var(--qa-gold-tint)]/55 px-5 py-5 sm:px-7">
                  <p className="text-xs text-muted-foreground">{fmtDate(selected.created_at)}</p>
                  <h2 className="mt-1 font-display text-2xl text-foreground">{selected.title || "Untitled reflection"}</h2>
                  <p className="mt-3 line-clamp-6 whitespace-pre-wrap text-sm leading-7 text-foreground/80">{selected.content}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selected.mood ? (
                      <Tag icon={MOOD_ICONS[selected.mood.toLowerCase()] ?? Sparkles} label={selected.mood} />
                    ) : null}
                    {selected.prompt ? <Tag icon={Sparkles} label="Written from a prompt" /> : null}
                  </div>
                </div>

                <div className="p-5 sm:p-7">
                  {!reading ? (
                    <div className="text-center">
                      <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-gold/15 text-[var(--gold-deep)]"><WandSparkles className="h-6 w-6" /></span>
                      <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
                        Only this selected entry is shared when you ask for a reading. The reading is not saved.
                      </p>
                      <Button type="button" variant="gold" onClick={beginReading} disabled={loading} className="mt-5 w-full rounded-full sm:w-auto sm:px-8">
                        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading gently…</> : <><Sparkles className="h-4 w-4" /> Read this entry</>}
                      </Button>
                      <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground"><LockKeyhole className="h-3.5 w-3.5" /> Private and requested by you</p>
                    </div>
                  ) : (
                    <div aria-live="polite">
                      <div className="mb-5 flex items-center gap-2 text-[var(--gold-deep)]"><Sparkles className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-wider">Your gentle reflection</span></div>
                      <div className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-foreground prose-p:text-foreground/85 prose-li:text-foreground/85 prose-strong:text-foreground">
                        <ReactMarkdown>{reading}</ReactMarkdown>
                      </div>
                      <div className="mt-6 border-t border-gold/25 pt-4 text-xs leading-relaxed text-muted-foreground">
                        This is gentle education, not therapy or diagnosis. Keep only what feels useful — your own understanding matters most. If you're in danger, please contact local emergency services or a crisis line now.
                      </div>
                      <Button type="button" variant="goldOutline" onClick={beginReading} disabled={loading} className="mt-4 rounded-full">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Reflect again
                      </Button>
                    </div>
                  )}
                  {error ? <p role="alert" className="mt-4 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">{error}</p> : null}
                </div>
              </section>
            ) : null}
          </div>
        )}
      </div>

      <AiConsentDialog
        open={consentOpen}
        onDecline={() => setConsentOpen(false)}
        onAgree={() => { setConsentOpen(false); void requestReading(); }}
      />
    </main>
  );
}

function Tag({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-card/75 px-2.5 py-1 text-xs text-foreground">
      <Icon className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
      {label}
    </span>
  );
}
